import { Button, Drawer, Modal } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import { PencilIcon, PlusIcon, TrashIcon } from "@phosphor-icons/react";
import { useCallback, useEffect, useState } from "react";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import { describePerformanceDeletion } from "./performance-deletion";

type Lot = {
  id: number;
  name: string;
  shortName: string | null;
};

type Performance = {
  id: number;
  lotId: number;
  name: string;
  order: number;
  interventionCount: number;
};

export type PerformancesDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  yardId: number;
  /** Whether the yard is still in its preparation period. */
  inPreparation: boolean;
  onSuccess?: () => void;
};

export function PerformancesDrawer({
  open,
  onOpenChange,
  yardId,
  inPreparation,
  onSuccess,
}: PerformancesDrawerProps) {
  const [lots, setLots] = useState<Lot[]>([]);
  const [performances, setPerformances] = useState<Performance[]>([]);
  const [busy, setBusy] = useState(false);

  // Add form per lot
  const [addLotId, setAddLotId] = useState<number | null>(null);
  const [newName, setNewName] = useState("");

  // Inline edit
  const [editId, setEditId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [editOrder, setEditOrder] = useState("");

  const [deleteTarget, setDeleteTarget] = useState<Performance | null>(null);

  const fetchData = useCallback(async () => {
    const [lotsRes, perfRes] = await Promise.all([
      apiClient.GET("/api/admin/yards/lots", {
        params: { query: { yardId } },
      }),
      apiClient.GET("/api/admin/yards/performances", {
        params: { query: { yardId } },
      }),
    ]);
    if (lotsRes.data) setLots(lotsRes.data.data.lots);
    if (perfRes.data) setPerformances(perfRes.data.data.performances);
  }, [yardId]);

  useEffect(() => {
    if (open) {
      fetchData();
      setAddLotId(null);
      setEditId(null);
    }
  }, [open, fetchData]);

  const refresh = async () => {
    await fetchData();
    onSuccess?.();
  };

  const createPerformance = async (lotId: number) => {
    if (!newName.trim()) {
      toaster.error({ title: "Erreur", description: "Le nom est requis" });
      return;
    }
    setBusy(true);
    const { error } = await apiClient.POST("/api/admin/yards/performances", {
      body: { yardId, lotId, name: newName.trim() },
    });
    setBusy(false);
    if (error) {
      toaster.error({
        title: "Erreur",
        description: error.error || "Échec de la création",
      });
      return;
    }
    toaster.success({ title: "Succès", description: "Prestation créée" });
    setAddLotId(null);
    setNewName("");
    refresh();
  };

  const saveEdit = async (performanceId: number) => {
    if (!editName.trim()) {
      toaster.error({ title: "Erreur", description: "Le nom est requis" });
      return;
    }
    setBusy(true);
    const { error } = await apiClient.PATCH(
      "/api/admin/yards/performances/{id}",
      {
        params: { path: { id: performanceId } },
        body: {
          name: editName.trim(),
          order: editOrder === "" ? 0 : Number(editOrder),
        },
      },
    );
    setBusy(false);
    if (error) {
      toaster.error({
        title: "Erreur",
        description: error.error || "Échec de la modification",
      });
      return;
    }
    toaster.success({ title: "Succès", description: "Prestation modifiée" });
    setEditId(null);
    refresh();
  };

  const confirmDelete = async () => {
    if (isNil(deleteTarget)) return;
    setBusy(true);
    const { error } = await apiClient.DELETE(
      "/api/admin/yards/performances/{id}",
      { params: { path: { id: deleteTarget.id } } },
    );
    setBusy(false);
    if (error) {
      toaster.error({
        title: "Erreur",
        description: error.error || "Échec de la suppression",
      });
      return;
    }
    toaster.success({ title: "Succès", description: "Prestation supprimée" });
    setDeleteTarget(null);
    refresh();
  };

  const deletion = describePerformanceDeletion(
    deleteTarget?.name ?? "",
    deleteTarget?.interventionCount ?? 0,
    inPreparation,
  );

  return (
    <Drawer.Root
      open={open}
      onOpenChange={(details) => onOpenChange(details.open)}
    >
      <Drawer.Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content>
            <div className="flex items-center justify-between border-b border-zinc-200 p-6 dark:border-zinc-800">
              <div>
                <Drawer.Title>Prestations</Drawer.Title>
                <Drawer.Description>
                  Gérez les prestations de chaque lot
                </Drawer.Description>
              </div>
              <Drawer.CloseTrigger />
            </div>

            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-6">
              {lots.length === 0 ? (
                <p className="text-sm text-zinc-400">
                  Aucun lot. Créez d'abord des lots.
                </p>
              ) : (
                lots.map((lot) => {
                  const lotPerformances = performances
                    .filter((p) => p.lotId === lot.id)
                    .sort((a, b) => a.order - b.order);
                  return (
                    <div
                      key={lot.id}
                      className="rounded-lg border border-zinc-200 dark:border-zinc-800"
                    >
                      <div className="border-b border-zinc-200 bg-zinc-50 px-3 py-2 text-sm font-medium text-zinc-900 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-100">
                        {lot.shortName ? `${lot.shortName} — ` : ""}
                        {lot.name}
                      </div>
                      <div className="space-y-1 p-3">
                        {lotPerformances.length === 0 && (
                          <p className="text-xs text-zinc-400">
                            Aucune prestation
                          </p>
                        )}
                        {lotPerformances.map((perf) =>
                          editId === perf.id ? (
                            <div
                              key={perf.id}
                              className="flex items-center gap-2"
                            >
                              <input
                                type="number"
                                min={0}
                                value={editOrder}
                                onChange={(e) => setEditOrder(e.target.value)}
                                className="w-16 rounded-md border border-zinc-300 px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
                              />
                              <input
                                type="text"
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                className="min-w-0 flex-1 rounded-md border border-zinc-300 px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
                              />
                              <Button
                                onClick={() => saveEdit(perf.id)}
                                disabled={busy}
                              >
                                OK
                              </Button>
                              <Button plain onClick={() => setEditId(null)}>
                                Annuler
                              </Button>
                            </div>
                          ) : (
                            <div
                              key={perf.id}
                              className="flex items-center justify-between py-1 text-sm"
                            >
                              <span className="text-zinc-700 dark:text-zinc-300">
                                <span className="mr-2 text-xs text-zinc-400">
                                  {perf.order}
                                </span>
                                {perf.name}
                              </span>
                              <div className="flex gap-1">
                                <Button
                                  plain
                                  onClick={() => {
                                    setEditId(perf.id);
                                    setEditName(perf.name);
                                    setEditOrder(String(perf.order));
                                  }}
                                  aria-label="Modifier"
                                >
                                  <PencilIcon className="size-4" />
                                </Button>
                                <Button
                                  plain
                                  onClick={() => setDeleteTarget(perf)}
                                  aria-label="Supprimer"
                                >
                                  <TrashIcon className="size-4" />
                                </Button>
                              </div>
                            </div>
                          ),
                        )}

                        {addLotId === lot.id ? (
                          <div className="mt-2 flex items-center gap-2">
                            <input
                              type="text"
                              value={newName}
                              onChange={(e) => setNewName(e.target.value)}
                              placeholder="Nom de la prestation"
                              className="min-w-0 flex-1 rounded-md border border-zinc-300 px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
                            />
                            <Button
                              onClick={() => createPerformance(lot.id)}
                              disabled={busy || !newName}
                            >
                              Ajouter
                            </Button>
                            <Button plain onClick={() => setAddLotId(null)}>
                              Annuler
                            </Button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => {
                              setAddLotId(lot.id);
                              setNewName("");
                            }}
                            className="mt-1 flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline dark:text-blue-400"
                          >
                            <PlusIcon className="size-3.5" />
                            Ajouter une prestation
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </Drawer.Content>
        </Drawer.Positioner>
      </Drawer.Portal>

      <Modal.Root
        open={isNotNil(deleteTarget)}
        onOpenChange={(details) => {
          if (!details.open) setDeleteTarget(null);
        }}
      >
        <Modal.Portal>
          <Modal.Backdrop />
          <Modal.Positioner>
            <Modal.Content size="sm">
              <Modal.Header>
                <Modal.Title>{deletion.title}</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <p className="text-sm text-zinc-600 dark:text-zinc-400">
                  {deletion.message}
                </p>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  outline
                  onClick={() => setDeleteTarget(null)}
                  disabled={busy}
                >
                  {deletion.canDelete ? "Annuler" : "Fermer"}
                </Button>
                {deletion.canDelete && (
                  <Button color="red" onClick={confirmDelete} disabled={busy}>
                    {busy ? "Suppression..." : "Supprimer"}
                  </Button>
                )}
              </Modal.Footer>
            </Modal.Content>
          </Modal.Positioner>
        </Modal.Portal>
      </Modal.Root>
    </Drawer.Root>
  );
}
